import { useEffect, useState, type ReactNode, type RefObject } from 'react';

/** Section d'une page de paramètres. Son libellé apparaît dans le sommaire en haut de page. */
export function TocSection({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div data-toc={label} className={className} style={{ scrollMarginTop: 16 }}>
      {children}
    </div>
  );
}

/**
 * Sommaire d'une page de paramètres : un lien par section (`TocSection`) présente à l'écran.
 * Rien si la page a moins de deux sections.
 */
export function SettingsToc({ containerRef }: { containerRef: RefObject<HTMLElement | null> }) {
  const [labels, setLabels] = useState<string[]>([]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const read = () => {
      const next = [...root.querySelectorAll<HTMLElement>('[data-toc]')].map((el) => el.dataset.toc ?? '');
      setLabels((prev) => (prev.join('|') === next.join('|') ? prev : next));
    };
    read();
    // Une section peut arriver après un chargement, ou dépendre d'un réglage.
    const observer = new MutationObserver(read);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [containerRef]);

  if (labels.length < 2) return null;

  function go(label: string) {
    const target = [...(containerRef.current?.querySelectorAll<HTMLElement>('[data-toc]') ?? [])].find(
      (el) => el.dataset.toc === label,
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <nav aria-label="Sections de la page" className="mb-6 flex flex-wrap items-center gap-x-1 gap-y-1 border-b border-cime-border pb-3">
      <span className="mr-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">Dans cette page</span>
      {labels.map((label) => (
        <button
          key={label}
          type="button"
          onClick={() => go(label)}
          className="cursor-pointer rounded-full px-2.5 py-1 text-[13px] text-cime-text-primary transition-colors hover:bg-[rgba(4,52,41,0.07)] hover:text-cime-primary"
        >
          {label}
        </button>
      ))}
    </nav>
  );
}
